import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const ProgressContext = createContext(null);

export const useProgress = () => {
    const context = useContext(ProgressContext);
    if (!context) {
        throw new Error('useProgress must be used within a ProgressProvider');
    }
    return context;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProgressProvider = ({ children }) => {
    const { user, isAuthenticated } = useAuth();
    const [completedLessons, setCompletedLessons] = useState(new Set());
    const [currentLesson, setCurrentLesson] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [lastSynced, setLastSynced] = useState(null);

    // Load progress from server when user logs in
    const fetchProgress = useCallback(async () => {
        if (!isAuthenticated) return;
        
        try {
            const response = await fetch(`${API_URL}/api/progress`, {
                credentials: 'include'
            });
            
            if (response.ok) {
                const data = await response.json();
                setCompletedLessons(new Set(data.completed_lessons || []));
                setLastSynced(data.updated_at);
            }
        } catch (error) {
            console.error('Failed to fetch progress:', error);
            // Fall back to localStorage if server fails
            const localKey = `progress_${user?.user_id}`;
            const stored = localStorage.getItem(localKey);
            if (stored) {
                setCompletedLessons(new Set(JSON.parse(stored)));
            }
        }
    }, [isAuthenticated, user?.user_id]);

    useEffect(() => {
        if (isAuthenticated && user) {
            fetchProgress();
            
            // Migrate localStorage progress to server if exists
            const localKey = `progress_${user.user_id}`;
            const legacyKey = `progress_user_${user.email?.replace(/[@.]/g, '_')}`;
            const storedLocal = localStorage.getItem(localKey);
            const storedLegacy = localStorage.getItem(legacyKey);
            const localProgress = storedLocal ? JSON.parse(storedLocal) : 
                                  storedLegacy ? JSON.parse(storedLegacy) : null;
            
            if (localProgress && localProgress.length > 0) {
                // Sync local progress to server
                syncLocalProgressToServer(localProgress);
            }
        } else {
            setCompletedLessons(new Set());
        }
    }, [isAuthenticated, user, fetchProgress]);

    const syncLocalProgressToServer = async (localLessons) => {
        try {
            const response = await fetch(`${API_URL}/api/progress/sync`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ completed_lessons: localLessons })
            });
            
            if (response.ok) {
                const data = await response.json();
                setCompletedLessons(new Set(data.completed_lessons || []));
                // Clear local storage after successful sync
                if (user?.user_id) {
                    localStorage.removeItem(`progress_${user.user_id}`);
                }
                console.log('Local progress synced to server');
            }
        } catch (error) {
            console.error('Failed to sync local progress:', error);
        }
    };

    const markLessonComplete = async (lessonId) => {
        // Optimistic update
        setCompletedLessons(prev => {
            const newSet = new Set(prev);
            newSet.add(lessonId);
            return newSet;
        });

        if (isAuthenticated) {
            setSyncing(true);
            try {
                const response = await fetch(`${API_URL}/api/progress`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify({ lesson_id: lessonId, completed: true })
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setCompletedLessons(new Set(data.completed_lessons || []));
                    setLastSynced(data.updated_at);
                }
            } catch (error) {
                console.error('Failed to update progress:', error);
                // Keep optimistic update, will sync later
            } finally {
                setSyncing(false);
            }
        } else {
            // Fallback to localStorage for non-authenticated users
            const stored = localStorage.getItem('progress_guest');
            const lessons = stored ? new Set(JSON.parse(stored)) : new Set();
            lessons.add(lessonId);
            localStorage.setItem('progress_guest', JSON.stringify([...lessons]));
        }
    };

    const markLessonIncomplete = async (lessonId) => {
        // Optimistic update
        setCompletedLessons(prev => {
            const newSet = new Set(prev);
            newSet.delete(lessonId);
            return newSet;
        });

        if (isAuthenticated) {
            setSyncing(true);
            try {
                const response = await fetch(`${API_URL}/api/progress`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify({ lesson_id: lessonId, completed: false })
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setCompletedLessons(new Set(data.completed_lessons || []));
                    setLastSynced(data.updated_at);
                }
            } catch (error) {
                console.error('Failed to update progress:', error);
            } finally {
                setSyncing(false);
            }
        }
    };

    const isLessonComplete = (lessonId) => {
        return completedLessons.has(lessonId);
    };

    const getSectionProgress = (sectionId, lessons) => {
        const sectionLessons = lessons.filter(l => l.sectionId === sectionId);
        const completedCount = sectionLessons.filter(l => completedLessons.has(l.id)).length;
        return {
            completed: completedCount,
            total: sectionLessons.length,
            percentage: sectionLessons.length > 0 
                ? Math.round((completedCount / sectionLessons.length) * 100) 
                : 0
        };
    };

    const getOverallProgress = (lessons) => {
        const completedCount = lessons.filter(l => completedLessons.has(l.id)).length;
        return {
            completed: completedCount,
            total: lessons.length,
            percentage: lessons.length > 0 
                ? Math.round((completedCount / lessons.length) * 100) 
                : 0
        };
    };

    const value = {
        completedLessons,
        currentLesson,
        setCurrentLesson,
        markLessonComplete,
        markLessonIncomplete,
        isLessonComplete,
        getSectionProgress,
        getOverallProgress,
        syncing,
        lastSynced,
        refreshProgress: fetchProgress
    };

    return (
        <ProgressContext.Provider value={value}>
            {children}
        </ProgressContext.Provider>
    );
};

export default ProgressContext;
